import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Loader2, Percent, Plus, Store, Tag, Trash2 } from "lucide-react";
import { Btn, Pill, Section, Toggle, TONES, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";

const TONE = TONES.marketing;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); }
    catch { return iso.slice(0, 10); }
};
const datePart = (iso) => (iso || "").slice(0, 10);

const STATE = {
    live: ["Running now", "border-[#067647] bg-[#ecfdf3] text-[#067647]"],
    scheduled: ["Starts later", "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]"],
    ended: ["Finished", "border-[#d0d0d0] bg-white text-[#1a1a1a]"],
    off: ["Switched off", "border-[#d0d0d0] bg-white text-[#1a1a1a]"],
};
const SCOPES = [["shop", "Everything in my shop"], ["lines", "Whole counters"], ["products", "Just the products I tick"]];
const PRESETS = [["weekend", "This weekend"], ["today", "Today only"], ["week", "7 days"], ["month", "30 days"]];
const blank = { id: null, title: "", percent: 15, scope: "shop", lines: [], products: [], starts_at: "", ends_at: "", active: true };

function Row({ sale, onToggle, onEdit, onDelete, busy }) {
    const [label, cls] = STATE[sale.state] || STATE.off;
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-black/10 px-3 py-2.5 first:border-t-0"
            data-testid={`sale-row-${sale.id}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-red-700/30 bg-red-50 font-mono text-[12px] font-black text-red-800">
                {Math.round(sale.percent)}%
            </span>
            <div className="min-w-[200px] flex-1">
                <p className="font-display text-sm font-bold text-zinc-900">{sale.title}</p>
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-700">
                    {sale.covers_label} · {sale.orders} order{sale.orders === 1 ? "" : "s"} · {money(sale.saved)} given back
                </p>
            </div>
            <span className={`rounded border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] ${cls}`}
                data-testid={`sale-state-${sale.id}`}>{label}</span>
            {(sale.starts_at || sale.ends_at) && (
                <span className="inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-700">
                    <CalendarDays size={11} />{sale.starts_at ? day(sale.starts_at) : "now"} → {sale.ends_at ? day(sale.ends_at) : "no end"}
                </span>
            )}
            <Toggle on={sale.active} disabled={busy} testid={`sale-toggle-${sale.id}`} onChange={() => onToggle(sale)} />
            <Btn onClick={() => onEdit(sale)} testid={`sale-edit-${sale.id}`}>Edit</Btn>
            <button onClick={() => onDelete(sale)} disabled={busy} data-testid={`sale-delete-${sale.id}`}
                className="text-zinc-600 transition-colors hover:text-red-600"><Trash2 size={14} /></button>
        </div>
    );
}

/** A shop's own markdowns — live the second they're switched on, no approval. */
export default function SalesPanel({ onCount }) {
    const [data, setData] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [form, setForm] = useState(blank);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api.get("/store/sales").then(({ data }) => { setData(data); onCount?.(data.live); })
            .catch((e) => setData({ error: formatApiError(e.response?.data?.detail), sales: [], lines: [] }));
    }, [onCount]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/store/catalog").then(({ data }) => setCatalog(data)).catch(() => setCatalog({ products: [] }));
    }, []);

    const run = async (fn, msg) => {
        setBusy(true);
        try { await fn(); load(); if (msg) toast.success(msg); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const save = () => run(async () => {
        const { data: res } = await api.post("/store/sales", {
            ...form, percent: Number(form.percent) || 0,
            products: form.products.map((p) => ({ kind: p.kind, slug: p.slug, name: p.name })),
        });
        toast.success(res.note);
        setForm(blank); setOpen(false);
    });

    const preset = async (key) => {
        const { data: res } = await api.get(`/store/sales/window/${key}`);
        setForm((f) => ({ ...f, starts_at: res.starts_at, ends_at: res.ends_at }));
    };

    const tick = (p) => setForm((f) => ({
        ...f,
        products: f.products.some((x) => x.slug === p.slug)
            ? f.products.filter((x) => x.slug !== p.slug)
            : [...f.products, { kind: p.kind, slug: p.slug, name: p.name }],
    }));

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

    const products = catalog?.products || [];

    return (
        <div data-testid="sales-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-zinc-800">
                    Tick what's on sale, set the percent, give it a start and an end — the struck-through price shows
                    on the cards, in the designers and at the register the second you switch it on.
                </p>
                <Btn tone="solid" testid="sale-new" onClick={() => { setForm(blank); setOpen(true); }}>
                    <Plus size={11} /> new sale
                </Btn>
            </div>

            {open && (
                <div className="mt-3">
                    <Section title={form.id ? "Edit this sale" : "Set up a sale"} tone={TONE} testid="sale-form"
                        note="No approval needed — your shop, your price">
                        <div className="grid gap-3 sm:grid-cols-[1fr_130px]">
                            <label className="block">
                                <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">What the sign says</span>
                                <input className={inputCls} data-testid="sale-title" placeholder="Labor Day weekend — tees are 20% off"
                                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Percent off</span>
                                <span className="relative block">
                                    <input type="number" min="1" max="90" className={inputCls} data-testid="sale-percent"
                                        value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} />
                                    <Percent size={13} className="pointer-events-none absolute right-3 top-3 text-zinc-500" />
                                </span>
                            </label>
                        </div>

                        <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">What's on sale</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="sale-scopes">
                            {SCOPES.map(([key, label]) => (
                                <Pill key={key} active={form.scope === key} testid={`sale-scope-${key}`}
                                    onClick={() => setForm({ ...form, scope: key })}>{label}</Pill>
                            ))}
                        </div>

                        {form.scope === "lines" && (
                            <div className="mt-2.5 grid gap-1.5 sm:grid-cols-3" data-testid="sale-lines">
                                {(data.lines || []).map((l) => (
                                    <label key={l.key} className="flex cursor-pointer items-center gap-2 rounded border border-black/10 px-2.5 py-1.5 text-sm text-zinc-900">
                                        <input type="checkbox" className="h-4 w-4 accent-zinc-900" data-testid={`sale-line-${l.key}`}
                                            checked={form.lines.includes(l.key)}
                                            onChange={(e) => setForm((f) => ({
                                                ...f, lines: e.target.checked ? [...f.lines, l.key] : f.lines.filter((k) => k !== l.key),
                                            }))} />
                                        {l.label}
                                    </label>
                                ))}
                            </div>
                        )}

                        {form.scope === "products" && (
                            <div className="mt-2.5 max-h-56 overflow-y-auto rounded border border-black/10" data-testid="sale-products">
                                {!products.length && <p className="p-3 text-sm text-zinc-800">Load your catalog first — nothing to mark down yet.</p>}
                                {products.map((p) => (
                                    <label key={`${p.kind}-${p.slug}`} data-testid={`sale-product-${p.slug}`}
                                        className="flex cursor-pointer items-center gap-2.5 border-b border-black/[0.07] px-2.5 py-1.5 text-sm text-zinc-900 last:border-0 hover:bg-zinc-50">
                                        <input type="checkbox" className="h-4 w-4 accent-zinc-900"
                                            checked={form.products.some((x) => x.slug === p.slug)} onChange={() => tick(p)} />
                                        <span className="flex-1 truncate">{p.name}</span>
                                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-700">
                                            {p.decoration ? p.decoration.replace("_", " ") : p.category}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}

                        <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">When it runs</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="sale-presets">
                            {PRESETS.map(([key, label]) => (
                                <Pill key={key} testid={`sale-preset-${key}`} onClick={() => preset(key)}>{label}</Pill>
                            ))}
                        </div>
                        <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
                            <label className="block">
                                <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Starts</span>
                                <input type="date" className={inputCls} data-testid="sale-starts"
                                    value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Ends</span>
                                <input type="date" className={inputCls} data-testid="sale-ends"
                                    value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
                            </label>
                        </div>
                        <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-sm text-zinc-900">
                            <input type="checkbox" className="h-4 w-4 accent-zinc-900" data-testid="sale-active"
                                checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                            Switched on · leave both dates blank and it runs until you switch it off
                        </label>

                        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-black/10 pt-3">
                            <GreenButton onClick={save} disabled={busy} data-testid="sale-save">
                                {busy ? <Loader2 size={15} className="animate-spin" /> : <Tag size={15} />}
                                {form.id ? "Save this sale" : "Put it on sale"}
                            </GreenButton>
                            <Btn onClick={() => { setOpen(false); setForm(blank); }} testid="sale-cancel">cancel</Btn>
                        </div>
                    </Section>
                </div>
            )}

            <div className="mt-3">
                {(data.sales || []).length ? (
                    <Section title={`${data.sales.length} sale${data.sales.length === 1 ? "" : "s"} on the books`}
                        tone={TONE} pad={false} testid="sales-list"
                        note="One sale per product — if two overlap the customer gets the deeper one">
                        {data.sales.map((s) => (
                            <Row key={s.id} sale={s} busy={busy}
                                onEdit={(sale) => {
                                    setForm({
                                        id: sale.id, title: sale.title, percent: sale.percent, scope: sale.scope,
                                        lines: sale.lines || [], products: sale.products || [],
                                        starts_at: datePart(sale.starts_at), ends_at: datePart(sale.ends_at), active: sale.active,
                                    });
                                    setOpen(true);
                                }}
                                onToggle={(sale) => run(() => api.post(`/store/sales/${sale.id}/toggle`))}
                                onDelete={(sale) => run(() => api.delete(`/store/sales/${sale.id}`), "Sale removed")} />
                        ))}
                    </Section>
                ) : (
                    <div className="rounded-lg border border-black/10 bg-white p-8 text-center" data-testid="sales-empty">
                        <Store size={28} className="mx-auto text-zinc-400" />
                        <p className="mt-2.5 text-sm text-zinc-800">No sale running. Put 20% on your white tees for a weekend and see what happens.</p>
                        <button onClick={() => { setForm(blank); setOpen(true); }} data-testid="sales-empty-new"
                            className="mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                            set up a sale →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
